import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchShopProducts, fetchShopSettings } from "./api";
import LandingNav from "./components/layout/LandingNav";
import SiteFooter from "./components/ui/SiteFooter";
import CartDrawer from "./components/webshop/CartDrawer";
import ProductCard from "./components/webshop/ProductCard";
import ProductModal from "./components/webshop/ProductModal";
import WebshopComingSoon from "./components/webshop/WebshopComingSoon";
import { addToCart, buildCartLines, pruneCart, useCart } from "./shop/cartStore";
import { translations } from "./translations";

const CATEGORY_ORDER = ["coffee", "homemade", "merch", "other"];

function getPageLang() {
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang === "en" ? "en" : "hu";
}

function WebshopPage() {
  const [lang, setLang] = useState(getPageLang);
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | closed | error
  const [coffeePriceHuf, setCoffeePriceHuf] = useState(0); // 0 = coffee not on sale
  const [category, setCategory] = useState("all");
  const [activeProduct, setActiveProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const { items: cartItems } = useCart();
  const siteT = translations[lang];
  const t = siteT.shop;

  useEffect(() => {
    document.title = `${t.title} — Mizu Brunch & Coffee`;
    document.documentElement.lang = lang;
  }, [lang, t.title]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await fetchShopProducts();
        if (cancelled) return;
        if (!data.webshopEnabled) {
          setStatus("closed");
          const shop = await fetchShopSettings().catch(() => null);
          if (!cancelled) {
            setCoffeePriceHuf(
              shop?.coffeeEnabled && shop.coffeePriceHuf >= 175 ? shop.coffeePriceHuf : 0,
            );
          }
          return;
        }
        setProducts(data.products || []);
        pruneCart((data.products || []).map((product) => product.id));
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleLangChange(nextLang) {
    setLang(nextLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", nextLang);
    window.history.replaceState({}, "", url);
  }

  const categories = useMemo(
    () => CATEGORY_ORDER.filter((id) => products.some((product) => product.category === id)),
    [products],
  );
  const visibleProducts =
    category === "all" ? products : products.filter((product) => product.category === category);
  const cartIds = new Set(cartItems.map((item) => item.productId));
  const cartLines = buildCartLines(cartItems, products, lang);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);
  const closeProduct = useCallback(() => setActiveProduct(null), []);

  function handleModalAdd(product, quantity) {
    addToCart(product.id, quantity);
    setActiveProduct(null);
    setCartOpen(true);
  }

  if (status === "closed") {
    return (
      <div className="landing shop-page">
        <LandingNav lang={lang} onLangChange={handleLangChange} t={siteT} hrefPrefix="/" />
        <main className="shop-soon-main">
          <WebshopComingSoon t={t.soon} lang={lang} coffeePriceHuf={coffeePriceHuf} />
        </main>
        <SiteFooter t={siteT.visitUs} />
      </div>
    );
  }

  return (
    <div className="landing shop-page">
      <LandingNav
        lang={lang}
        onLangChange={handleLangChange}
        t={siteT}
        hrefPrefix="/"
        onOpenCart={openCart}
      />

      <header className="shop-hero">
        <div className="container">
          <p className="shop-hero-eyebrow">{t.eyebrow}</p>
          <h1 className="shop-hero-title">{t.title}</h1>
          <p className="shop-hero-subtitle">{t.subtitle}</p>
        </div>
      </header>

      <main className="shop-main">
        <div className="container">
          {categories.length > 1 ? (
            <div className="shop-filters" role="tablist" aria-label={t.title}>
              {["all", ...categories].map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={category === id}
                  className={category === id ? "shop-filter is-active" : "shop-filter"}
                  onClick={() => setCategory(id)}
                >
                  {t.categories[id]}
                </button>
              ))}
            </div>
          ) : null}

          {status === "loading" ? <p className="shop-status">{t.loading}</p> : null}
          {status === "error" ? <p className="shop-status">{t.loadError}</p> : null}
          {status === "ready" && products.length === 0 ? (
            <p className="shop-status">{t.empty}</p>
          ) : null}

          <div className="shop-grid">
            {visibleProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                t={t}
                lang={lang}
                inCart={cartIds.has(product.id)}
                onOpen={setActiveProduct}
                onAdd={(item) => addToCart(item.id, 1)}
              />
            ))}
          </div>
        </div>
      </main>

      <SiteFooter t={siteT.visitUs} />

      {activeProduct ? (
        <ProductModal
          product={activeProduct}
          t={t}
          lang={lang}
          onClose={closeProduct}
          onAdd={handleModalAdd}
        />
      ) : null}

      <CartDrawer open={cartOpen} lines={cartLines} t={t} lang={lang} onClose={closeCart} />
    </div>
  );
}

export default WebshopPage;
